import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { SocialLinks, SupportContact } from '../../core/models/core.model';
import { GateCountdown } from './gate-countdown';
import { GateWaitlistForm } from './gate-waitlist-form';

export type GateMode = 'maintenance' | 'prelaunch';

interface FeatureItem {
  title: string;
  body: string;
  icon: 'shield' | 'lock' | 'globe';
}

const FEATURES: FeatureItem[] = [
  { icon: 'shield', title: 'Verified Partner Network', body: 'Every newspaper and agency is accredited before it can publish.' },
  { icon: 'lock', title: 'Secure Settlements', body: 'Payments and payouts are tracked end-to-end, down to the kobo.' },
  { icon: 'globe', title: 'Nationwide Reach', body: 'One workspace for every publication request, wherever it originates.' },
];

const SOCIAL_LABELS: Record<keyof SocialLinks, string> = {
  facebook: 'Facebook',
  x: 'X',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
};

/**
 * Full-page branded gate shown while the partner workspace is offline.
 * Rendered above the router-outlet by the root App component, so it covers
 * every route -- see Maintenance, which supplies its inputs from app config.
 *
 * One adaptive template rather than two page components: prelaunch and
 * maintenance need different tone and copy, not a different structure, and
 * a shared shell keeps both states visually consistent and easy to keep in
 * sync as the design evolves.
 */
@Component({
  selector: 'app-gate-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GateCountdown, GateWaitlistForm],
  styles: [
    `
      :host {
        display: block;
      }
      .gate-blob {
        position: absolute;
        border-radius: 9999px;
        filter: blur(64px);
        pointer-events: none;
      }
      .gate-link {
        transition: color 0.15s, background-color 0.15s;
      }
    `,
  ],
  template: `
    <div class="min-h-screen relative overflow-hidden" style="background: var(--background-primary)">
      <!-- Ambient background -->
      <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          class="gate-blob -top-[20%] -right-[10%] w-[55%] h-[55%]"
          [style.background]="mode() === 'prelaunch' ? 'color-mix(in srgb, var(--brand-accent) 22%, transparent)' : 'color-mix(in srgb, var(--brand-primary) 18%, transparent)'"
        ></div>
        <div
          class="gate-blob -bottom-[15%] -left-[10%] w-[45%] h-[45%]"
          style="background: color-mix(in srgb, var(--brand-primary) 12%, transparent)"
        ></div>
      </div>

      <div class="relative">
        <!-- Header -->
        <header class="border-b" style="border-color: var(--border-default); background: color-mix(in srgb, var(--card-background) 85%, transparent); backdrop-filter: blur(10px)">
          <div class="max-w-6xl mx-auto px-5 sm:px-8 h-[4.25rem] flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 min-w-0">
              <img src="assets/image.png" alt="NPR.ng" class="size-9 rounded-full object-contain shrink-0" width="36" height="36" />
              <span class="min-w-0">
                <span class="block text-[1.2rem] font-extrabold leading-none tracking-tight" style="color: var(--brand-primary)">
                  NPR.ng
                </span>
                <span class="hidden sm:block text-[10px] font-medium tracking-wide mt-0.5" style="color: var(--text-secondary)">
                  Partner Workspace
                </span>
              </span>
            </div>
            <p
              class="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] sm:text-xs font-semibold"
              [style.background]="badgeBg()"
              [style.color]="badgeColor()"
            >
              <span class="size-1.5 rounded-full" [style.background]="badgeColor()"></span>
              {{ mode() === 'prelaunch' ? 'Pre-launch' : 'Scheduled Maintenance' }}
            </p>
          </div>
        </header>

        <!-- Hero -->
        <section class="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10">
          <div class="max-w-2xl">
            <p class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-4" [style.color]="badgeColor()">
              {{ mode() === 'prelaunch' ? 'Coming soon' : "We'll be right back" }}
            </p>
            <h1 class="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight leading-[1.1]" style="color: var(--text-primary)">
              {{ heading() }}
            </h1>
            <p class="mt-5 text-base sm:text-lg leading-relaxed" style="color: var(--text-secondary)">
              {{ message() }}
            </p>

            @if (showCountdown()) {
              <div class="mt-8 max-w-md">
                <p class="text-xs font-semibold uppercase tracking-wider mb-2" style="color: var(--text-secondary)">
                  {{ mode() === 'prelaunch' ? 'Launching in' : 'Back online in' }}
                </p>
                <app-gate-countdown [endsAt]="endsAt()" (expired)="expired.emit()" />
              </div>
            }

            <div class="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#notify"
                class="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white gate-link"
                style="background: var(--brand-primary)"
              >
                {{ mode() === 'prelaunch' ? 'Join the pre-launch list' : "Get notified when we're back" }}
                <svg viewBox="0 0 20 20" fill="none" class="w-4 h-4">
                  <path d="M4 10h12m0 0l-5-5m5 5l-5 5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </a>
              <button
                type="button"
                (click)="recheck.emit()"
                [disabled]="checking()"
                class="text-sm font-medium gate-link disabled:opacity-60"
                style="color: var(--brand-primary)"
              >
                {{ checking() ? 'Checking…' : 'Check if we are back' }}
              </button>
            </div>
          </div>

          <!-- Trust strip -->
          <div class="mt-14 grid sm:grid-cols-3 gap-4">
            @for (item of features; track item.title) {
              <div class="rounded-2xl p-5 flex items-start gap-3" style="background: var(--card-background); border: 1px solid var(--border-default)">
                <span class="size-9 rounded-full flex items-center justify-center shrink-0" style="background: var(--background-secondary); color: var(--brand-primary)">
                  @switch (item.icon) {
                    @case ('shield') {
                      <svg viewBox="0 0 24 24" fill="none" class="w-5 h-5"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                    }
                    @case ('lock') {
                      <svg viewBox="0 0 24 24" fill="none" class="w-5 h-5"><rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" stroke-width="1.6" /><path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
                    }
                    @case ('globe') {
                      <svg viewBox="0 0 24 24" fill="none" class="w-5 h-5"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.6" /><path d="M3.5 12h17M12 3.5c2.5 2.4 4 5.4 4 8.5s-1.5 6.1-4 8.5c-2.5-2.4-4-5.4-4-8.5s1.5-6.1 4-8.5z" stroke="currentColor" stroke-width="1.6" /></svg>
                    }
                  }
                </span>
                <span>
                  <strong class="block text-sm font-semibold" style="color: var(--text-primary)">{{ item.title }}</strong>
                  <small class="block text-xs mt-1 leading-relaxed" style="color: var(--text-secondary)">{{ item.body }}</small>
                </span>
              </div>
            }
          </div>
        </section>

        <!-- Signup panel -->
        <section id="notify" class="max-w-6xl mx-auto px-5 sm:px-8 pb-14">
          <div class="rounded-3xl overflow-hidden" style="background: var(--brand-primary)">
            <div class="grid lg:grid-cols-2">
              <div class="p-8 sm:p-10">
                <p class="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                  Stay in the loop
                </p>
                <h2 class="mt-2 text-2xl font-bold text-white tracking-tight">
                  {{ mode() === 'prelaunch' ? 'Be first to onboard your organization' : "We'll let you know the moment we're back" }}
                </h2>
                <p class="mt-2 text-sm text-white/70 leading-relaxed max-w-md">
                  Leave your details and we'll email you the moment the partner workspace is
                  {{ mode() === 'prelaunch' ? 'ready to onboard new organizations' : 'back online' }}.
                </p>

                @if (contact().email || contact().phone) {
                  <div class="mt-6 flex flex-col gap-2 text-sm text-white/80">
                    @if (contact().email) {
                      <a [href]="'mailto:' + contact().email" class="inline-flex items-center gap-2 gate-link hover:text-white">
                        <svg viewBox="0 0 24 24" fill="none" class="w-4 h-4 shrink-0"><path d="M4 6h16v12H4z" stroke="currentColor" stroke-width="1.5" /><path d="M4 7l8 6 8-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                        {{ contact().email }}
                      </a>
                    }
                    @if (contact().phone) {
                      <a [href]="'tel:' + phoneHref()" class="inline-flex items-center gap-2 gate-link hover:text-white">
                        <svg viewBox="0 0 24 24" fill="none" class="w-4 h-4 shrink-0"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2C9.5 21 3 14.5 3 6a2 2 0 012-2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
                        {{ contact().phone }}
                      </a>
                    }
                  </div>
                }

                @if (visibleSocialLinks().length) {
                  <div class="mt-6 flex items-center gap-3">
                    @for (link of visibleSocialLinks(); track link.key) {
                      <a
                        [href]="link.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        [attr.aria-label]="link.label"
                        class="size-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white/80 gate-link hover:text-white"
                        style="background: rgba(255,255,255,0.1)"
                      >
                        {{ link.label.charAt(0) }}
                      </a>
                    }
                  </div>
                }
              </div>

              <div class="p-8 sm:p-10 lg:pl-0">
                <div class="rounded-2xl p-6" style="background: var(--card-background)">
                  <app-gate-waitlist-form [consentText]="consentText()" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer class="max-w-6xl mx-auto px-5 sm:px-8 pb-10 text-center">
          <p class="text-xs" style="color: var(--text-secondary)">
            &copy; {{ year }} News and Publication Registry. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  `,
})
export class GateView {
  readonly mode = input.required<GateMode>();
  readonly heading = input.required<string>();
  readonly message = input.required<string>();
  readonly endsAt = input<string | null>(null);
  readonly consentText = input<string>('');
  readonly contact = input<SupportContact>({});
  readonly socialLinks = input<SocialLinks>({});
  readonly checking = input(false);

  readonly expired = output<void>();
  readonly recheck = output<void>();

  readonly year = new Date().getFullYear();
  readonly features = FEATURES;

  readonly showCountdown = computed(() => !!this.endsAt());

  readonly badgeColor = computed(() => (this.mode() === 'prelaunch' ? 'var(--brand-accent)' : 'var(--brand-primary)'));
  readonly badgeBg = computed(() =>
    this.mode() === 'prelaunch'
      ? 'color-mix(in srgb, var(--brand-accent) 14%, transparent)'
      : 'color-mix(in srgb, var(--brand-primary) 10%, transparent)',
  );

  readonly phoneHref = computed(() => (this.contact().phone || '').replace(/[^\d+]/g, ''));

  readonly visibleSocialLinks = computed(() =>
    (Object.keys(SOCIAL_LABELS) as (keyof SocialLinks)[])
      .map((key) => ({ key, url: this.socialLinks()[key], label: SOCIAL_LABELS[key] }))
      .filter((link): link is { key: keyof SocialLinks; url: string; label: string } => !!link.url),
  );
}
